import hash from 'object-hash'
import { getApiBaseUrl } from '~/configs/environments/config'
import { createApiClient } from './api-client'
import { DEFAULT_ANY_MODULE_OPTIONS } from './modules/config'
import type { Options, UseModule } from './internal-types'
import type {
  AnyStarter,
  Starters,
  Modules,
  ModuleStorage,
  ParameterOptions,
  ModuleOptions
} from './modules/types/external'
export { Cs, Clans } from './modules/types/external'
export type {
  Achievements,
  Auth,
  Blog,
  Ranks,
  Ratings,
  Tasks,
  Codes,
  Common,
  Store,
  Profile,
  Members,
  Battlepass,
  Boxes,
  Quizzes,
  Referrals,
  Notifications,
  Privileges,
  Pickem
} from './modules/types/external'
export type {
  ListItemWrapperPublicInterface,
  SingleItemWrapperPublicInterface,
  ListWrapperPublicInterface,
  PagedListWrapperPublicInterface
} from './api-client/wrappers'

export const createSDK = async (options: Options) => {
  const host = await getApiBaseUrl()

  const apiClient = createApiClient({
    ...options.api,
    baseUrl: host
  })

  const starters: Starters = {}
  const moduleStorage: ModuleStorage = {}
  const moduleOptions: ModuleOptions = {}

  const useModule: UseModule = async (name, receivedModuleOptions = {}) => {
    type K = typeof name

    if (!starters[name]) {
      starters[name] = await import(`./modules/starters/${name}/index.ts`).then(
        result => result.default
      )
    }

    const starter = starters[name]

    if (!starter) {
      throw new ReferenceError(`Starter ${name} is not defined`)
    }

    if (!moduleStorage[name]) {
      moduleStorage[name] = new Map()
    }

    const moduleMap = moduleStorage[name]

    if (!moduleMap) {
      throw new ReferenceError(`Module map ${name} is not defined`)
    }

    const { defaultOptions: defaultOptionsInStarter } = starter as AnyStarter
    const defaultModuleOptions =
      typeof defaultOptionsInStarter === 'function'
        ? defaultOptionsInStarter(host)
        : defaultOptionsInStarter ?? {}

    const mergedOptions: ParameterOptions<K> = {
      apiClient,
      ...DEFAULT_ANY_MODULE_OPTIONS,
      ...(options.module ?? {}),
      ...defaultModuleOptions,
      ...(moduleOptions[name] ?? {}),
      ...receivedModuleOptions
    }

    const id = hash({
      ...mergedOptions,
      apiClient: mergedOptions.apiClient?.id
    })

    if (!moduleMap.has(id)) {
      if ((starter as AnyStarter).optionsRequired && !moduleOptions[name]) {
        throw new ReferenceError(`Module ${name} requires setting options`)
      }

      const module = starter.factory(mergedOptions as any, {
        module: useModule,
        projectId: options.projectId
      })

      moduleMap.set(id, module as Modules[K])
    }

    const module = moduleMap.get(id)

    if (!module) {
      throw new ReferenceError(`Module ${name}.${id} is not defined`)
    }

    return module
  }

  return {
    apiClient,
    module: useModule,

    setModuleOptions: <K extends keyof ModuleOptions>(
      name: K,
      receivedModuleOptions: ParameterOptions<K>
    ) => {
      moduleOptions[name] = receivedModuleOptions as ModuleOptions[K]
    }
  }
}
